import React, { useEffect } from "react";
import { isHTMLElement } from "../utils/isHTMLElement";
/**
 * Traps focus within a DOM node. Shifting focus with `Tab` and `Shift-Tab` will loop through all focusable elements of the node.
 * Nesting is handled naturally by the browser's event model — keydown events only bubble through DOM ancestors,
 * so a trap only fires when focus is actually inside its container.
 */
export function useFocusTrap(focusContainer: HTMLElement | null, enabled: boolean = true) {
    useEffect(() => {
        if (!focusContainer || !enabled)
            return;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== "Tab")
                return;
            const tabbableElements = Array.from(focusContainer.querySelectorAll(focusableSelector)).filter(isTabbableElement);
            if (tabbableElements.length === 0)
                return;
            const firstFocusableElement = tabbableElements[0];
            const lastFocusableElement = tabbableElements[tabbableElements.length - 1];
            // If focus is on a non-tabbable element, e.g. initial focus on a dialog container with tabIndex -1, move to first or last element depending on direction.
            if (document.activeElement && !isTabbableElement(document.activeElement)) {
                if (event.shiftKey) {
                    lastFocusableElement?.focus();
                }
                else {
                    firstFocusableElement?.focus();
                }
                event.preventDefault();
                return;
            }
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            const currentIndex = tabbableElements.indexOf(document.activeElement as HTMLElement);
            if (currentIndex === -1)
                return;
            // Always handle Tab ourselves so focus order follows DOM order. This prevents browsers from
            // skipping elements outside scrollable containers (e.g. dialog footer buttons).
            event.preventDefault();
            if (event.shiftKey) {
                const prevIndex = currentIndex === 0 ? tabbableElements.length - 1 : currentIndex - 1;
                tabbableElements[prevIndex].focus();
            }
            else {
                const nextIndex = currentIndex === tabbableElements.length - 1 ? 0 : currentIndex + 1;
                tabbableElements[nextIndex].focus();
            }
        };
        focusContainer.addEventListener("keydown", onKeyDown);
        return () => {
            focusContainer.removeEventListener("keydown", onKeyDown);
        };
    }, [focusContainer, enabled]);
}
export function useFocusFirstFocusableElement() {
    return React.useCallback((container: HTMLElement) => {
        const [firstFocusableElement] = Array.from(container.querySelectorAll(focusableSelector)).filter(isTabbableElement);
        if (firstFocusableElement) {
            firstFocusableElement.focus();
        }
        else if (container.tabIndex >= 0) {
            container.focus();
        }
    }, []);
}
// From https://github.com/testing-library/user-event/blob/main/src/utils/focus/selector.ts
const focusableSelector = ["input:not([type=hidden]):not([disabled])", "button:not([disabled])", "select:not([disabled])", "textarea:not([disabled])", "[contenteditable=\"\"]", "[contenteditable=\"true\"]", "a[href]", "[tabindex]:not([disabled])"].join(", ");
function isTabbableElement(element: Element): element is HTMLElement {
    if (!isHTMLElement(element))
        return false;
    if (element.tabIndex < 0)
        return false;
    // Exclude elements inside an inert subtree — inert removes them from tab order but tabIndex doesn't reflect it.
    if (element.closest("[inert]"))
        return false;
    if (!isVisible(element))
        return false;
    return true;
}
function isVisible(element: HTMLElement): boolean {
    if (element.hidden)
        return false;
    // Use offsetParent first (cheap). If null, use getComputedStyle to exclude only hidden elements (keeps position:fixed and jsdom).
    if (element.offsetParent === null) {
        const style = getComputedStyle(element);
        if (style.display === "none" || style.visibility === "hidden")
            return false;
    }
    return true;
}
